import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { sidePanelMaxWidth, sidePanelMinWidth, zIndex } from "@nova/constants"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { Resizable } from "re-resizable"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { SidePanelContent } from "./SidePanelContent"
import { FloatingExpandSidePanelButton } from "./SidePanelExpandCollapseButtons"
import {
    resetSidePanelState,
    selectIsSidePanelCollapsed,
    selectSidePanelWidth,
    setSidePanelWidth,
} from "./slice.new"
import { selectActiveContextMenu } from "features/popups/contextMenu"

export const useResizableSidePanelWidth = () => {
    const isSidePanelCollapsed = useAppSelector(selectIsSidePanelCollapsed)
    const sidePanelWidth = useAppSelector(selectSidePanelWidth)
    return isSidePanelCollapsed ? 0 : sidePanelWidth
}

export const SidePanel = memo(function SidePanel() {
    const dispatch = useAppDispatch()
    useEffect(() => {
        return () => {
            dispatch(resetSidePanelState())
        }
    }, [dispatch])
    return (
        <>
            <StickySidePanel />
            <FloatingSidePanel />
        </>
    )
})

function StickySidePanel() {
    const dispatch = useAppDispatch()
    const wrapperRef = useRef<HTMLDivElement>(null)
    const isSidePanelCollapsed = useAppSelector(selectIsSidePanelCollapsed)
    const [isResizing, setIsResizing] = useState(false)
    const [width, setWidth] = useState(sidePanelMinWidth)
    const getCursor = useCallback(() => {
        if (width === sidePanelMinWidth) {
            return "e-resize"
        }
        if (width === sidePanelMaxWidth) {
            return "w-resize"
        }
        return "ew-resize"
    }, [width])
    return (
        <div
            ref={wrapperRef}
            className={classNames("transition-all h-full pt-3 pb-2 bg-gray-800", {
                "pointer-events-none": isSidePanelCollapsed,
            })}
            style={{
                zIndex: zIndex.SidePanel,
                maxWidth: isSidePanelCollapsed ? 0 : sidePanelMaxWidth,
            }}
        >
            <div className="overflow-hidden h-full">
                <Resizable
                    minWidth={sidePanelMinWidth}
                    maxWidth={sidePanelMaxWidth}
                    defaultSize={{ width: sidePanelMinWidth }}
                    minHeight="100%"
                    maxHeight="100%"
                    className="!h-full"
                    onResize={(_, __, e) => {
                        setWidth(e.offsetWidth)
                    }}
                    onResizeStart={() => setIsResizing(true)}
                    onResizeStop={() => {
                        dispatch(
                            setSidePanelWidth({
                                width: wrapperRef.current?.clientWidth || sidePanelMinWidth,
                            })
                        )
                        setIsResizing(false)
                    }}
                    handleStyles={{
                        topRight: {
                            cursor: getCursor(),
                        },
                        right: {
                            right: 0,
                            cursor: getCursor(),
                        },
                        bottomRight: {
                            cursor: getCursor(),
                        },
                        ...["bottom", "bottomLeft", "left", "topLeft", "top"].reduce(
                            (acc, cur) => {
                                acc[cur] = {
                                    cursor: "default",
                                }
                                return acc
                            },
                            {} as Record<string, { cursor: "default" }>
                        ),
                    }}
                    handleComponent={{
                        right: (
                            <div className="w-full h-full group flex justify-end">
                                <div
                                    className={classNames(
                                        "bg-blue-700 w-[2px] mr-1 rounded-full group-hover:opacity-100 transition-opacity duration-100",
                                        {
                                            "opacity-0": !isResizing,
                                        }
                                    )}
                                />
                            </div>
                        ),
                    }}
                >
                    <div
                        className={classNames("text-white flex flex-col w-full gap-1 h-full", {
                            "opacity-0": isSidePanelCollapsed,
                        })}
                    >
                        <SidePanelContent />
                    </div>
                </Resizable>
            </div>
        </div>
    )
}

function FloatingSidePanel() {
    const isSidePanelCollapsed = useAppSelector(selectIsSidePanelCollapsed)
    const sidePanelWidth = useAppSelector(selectSidePanelWidth)
    const [isSidePanelFloating, setIsSidePanelFloating] = useState(false)
    const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelItem"))
    const pdfSnapshotOpen = useAppSelector(selectSnapshotOpen())
    const mouseEnterTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const mouseLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const handleMouseEnter = useCallback(() => {
        if (!isSidePanelCollapsed) return
        if (!mouseEnterTimeoutRef.current) {
            mouseEnterTimeoutRef.current = setTimeout(() => setIsSidePanelFloating(true), 100)
        }
        if (mouseLeaveTimeoutRef.current) {
            clearTimeout(mouseLeaveTimeoutRef.current)
            mouseLeaveTimeoutRef.current = null
        }
    }, [isSidePanelCollapsed])
    const handleMouseLeave = useCallback(() => {
        if (activeSidePanelContextMenu) return
        if (mouseEnterTimeoutRef.current) {
            clearTimeout(mouseEnterTimeoutRef.current)
            mouseEnterTimeoutRef.current = null
        }
        if (!mouseLeaveTimeoutRef.current) {
            mouseLeaveTimeoutRef.current = setTimeout(() => setIsSidePanelFloating(false), 500)
        }
    }, [activeSidePanelContextMenu])
    useEffect(() => {
        if (!isSidePanelCollapsed && isSidePanelFloating) {
            setIsSidePanelFloating(false)
            if (mouseEnterTimeoutRef.current) {
                clearTimeout(mouseEnterTimeoutRef.current)
                mouseEnterTimeoutRef.current = null
            }
            if (mouseLeaveTimeoutRef.current) {
                clearTimeout(mouseLeaveTimeoutRef.current)
                mouseLeaveTimeoutRef.current = null
            }
        }
    }, [isSidePanelCollapsed, isSidePanelFloating])
    if (!isSidePanelCollapsed || pdfSnapshotOpen) return null
    return (
        <div className="h-full relative" style={{ zIndex: zIndex.FloatingSidePanel }}>
            {/* Mouse trigger to open floating side panel */}
            <div
                className="absolute w-4 top-0 left-0 bottom-0"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            />
            {/* Floating expand button */}
            <div className="absolute top-2 left-2">
                <FloatingExpandSidePanelButton />
            </div>
            {/* Floating side panel */}
            <ClickAwayListenerV2
                onClickAway={handleMouseLeave}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}
                className="h-full relative"
            >
                <div
                    className={classNames(
                        "text-white absolute left-1 top-1 bottom-1 pt-3 pb-2 gap-3 flex flex-col bg-gray-800 rounded-lg border border-gray-700 transition-all",
                        {
                            "translate-x-[-115%]": !isSidePanelFloating,
                            "translate-x-0": isSidePanelFloating,
                        }
                    )}
                    style={{
                        width: `${sidePanelWidth}px`,
                        minWidth: `${sidePanelWidth}px`,
                        maxWidth: `${sidePanelWidth}px`,
                    }}
                >
                    <SidePanelContent />
                </div>
            </ClickAwayListenerV2>
        </div>
    )
}
